import SimpleTable from "../SimpleTable";
import organizationsListSignal from "./OrganizationsList.signal";
import { Button } from "react-bootstrap";
import OrganizationDetailsModal from "./components/OrganizationDetailsModal";
import {
    downloadSignupReport,
  fetchAndSetOrganizations,
  getRow,
  handleDetailClick,
  showShareOrganizationModal,
  handleOrganizationsFilterChange,
  handleOrganizationsAppliedFiltersChange,
  resetOrganizationsFilterInputs,
} from "./OrganizationsList.helpers";
import SimpleTableFilterDropdown from "../SimpleTableFilterDropdown";
import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router";
import history from "../../../history";
import ShareOrganizationModal from "./components/ShareOrganizationModal";
import { Location } from "history";
import $appSettings from "signals/AppSettings.signal";
import userAccountSignal from "signals/UserAccount.signal";
import PaginationItems from "../Pagination";

const HEADERS = [
  {
    key: 'id',
    title: 'ID',
    sortable: true,
  },
  {
    key: 'name',
    title: 'Name',
    sortable: true,
  },
  {
    key: 'contactName',
    title: 'Contact Name',
    sortable: true,
  },
  {
    key: 'contactEmail',
    title: 'Contact Email',
    sortable: true,
  },
  {
    key: 'status',
    title: 'Status',
  },
  {
    key: 'need',
    title: 'Need',
  },
  {
    key: 'createdDate',
    title: 'Created Date',
    sortable: true,
  },
  {
    key: 'action',
    title: 'Action',
  },
];

const FILTERS = [
  {
    type: 'integer',
    label: 'ID',
    key: 'id',
  },
  {
    type: 'text',
    label: 'Name',
    key: 'name',
  },
  {
    type: 'text',
    label: 'Contact Name',
    key: 'contactName',
  },
  {
    type: 'text',
    label: 'Contact Email',
    key: 'contactEmail',
  },
];

const BUSINESS_OWNERSHIP_FILTERS = [
  { type: 'checkbox' as const, label: 'Veteran-owned', key: 'veteranOwned' },
  { type: 'checkbox' as const, label: 'Woman-owned', key: 'womanOwned' },
  { type: 'checkbox' as const, label: 'Minority-owned', key: 'minorityOwned' },
];

type Props = {
  programMembershipOrFundId?: number;
  newPath: string;
  tableTitle?: string;
  refetchingFilter?: (location: Location) => boolean;
};

const OrganizationsList: React.FC<Props> = ({
  tableTitle,
  newPath,
  refetchingFilter,
}) => {
  const location = useLocation();
  const records = organizationsListSignal.value.accounts;
  const { totalPages, count } = organizationsListSignal.value;
  const { PORTAL_TYPES } = $appSettings.value.constants;
  const searchParams = Object.fromEntries(new URLSearchParams(location.search));
  const currentPage = searchParams.page ? Number(searchParams.page) : 1;

  const prevPathnameRef = useRef<string>(location.pathname);
  
  const getViewType = (pathname: string): string | null => {
    if (pathname.includes('/lenders')) return 'lenders';
    if (pathname.includes('/business')) return 'business';
    if (pathname.includes('/vc')) return 'vc';
    return null;
  };

  useEffect(() => {
    const prevViewType = getViewType(prevPathnameRef.current);
    const currentViewType = getViewType(location.pathname);
    
    const isDifferentViewType = prevViewType !== null && 
                                currentViewType !== null && 
                                prevViewType !== currentViewType;
    
    if (isDifferentViewType && currentViewType) {
      const currentFilters = organizationsListSignal.value;
      
      const updatedFiltersByViewType = { ...currentFilters.filtersByViewType };
      if (prevViewType) {
        updatedFiltersByViewType[prevViewType] = {
          dropdownFilters: { ...currentFilters.dropdownFilters },
          appliedFilters: { ...currentFilters.appliedFilters },
        };
      }
      
      const savedFilters = updatedFiltersByViewType[currentViewType];
      
      if (savedFilters) {
        organizationsListSignal.update({
          dropdownFilters: { ...savedFilters.dropdownFilters },
          appliedFilters: { ...savedFilters.appliedFilters },
          viewType: currentViewType,
          filtersByViewType: updatedFiltersByViewType,
        });
        
        const urlParams = new URLSearchParams(location.search);
        const filterKeys = [
          'id',
          'name',
          'contactName',
          'contactEmail',
          'veteranOwned',
          'womanOwned',
          'minorityOwned',
        ];

        filterKeys.forEach(key => urlParams.delete(key));
        
        Object.entries(savedFilters.appliedFilters).forEach(([key, value]) => {
          if (value) {
            urlParams.set(key, value);
          }
        });
        
        const newUrl = urlParams.toString() 
          ? `${location.pathname}?${urlParams.toString()}`
          : location.pathname;
        history.replace(newUrl);
      } else {
        organizationsListSignal.update({
          dropdownFilters: {},
          appliedFilters: {},
          viewType: currentViewType,
          filtersByViewType: updatedFiltersByViewType,
        });
        
        const urlParams = new URLSearchParams(location.search);
        const filterKeys = [
          'id',
          'name',
          'contactName',
          'contactEmail',
          'veteranOwned',
          'womanOwned',
          'minorityOwned',
        ];
        filterKeys.forEach(key => urlParams.delete(key));
        
        const newUrl = urlParams.toString() 
          ? `${location.pathname}?${urlParams.toString()}`
          : location.pathname;
        history.replace(newUrl);
      }
    }
    
    prevPathnameRef.current = location.pathname;
  }, [location.pathname]);

  const init = useCallback(() => {
    const urlParams = Object.fromEntries(new URLSearchParams(location.search));
    const filterKeys = [
      'id',
      'name',
      'contactName',
      'contactEmail',
      'veteranOwned',
      'womanOwned',
      'minorityOwned',
    ];
    const filtersFromUrl: Record<string, string> = {};
    
    filterKeys.forEach(key => {
      if (urlParams[key]) {
        filtersFromUrl[key] = urlParams[key];
      }
    });
    
    const currentViewType = getViewType(location.pathname);
    
    if (Object.keys(filtersFromUrl).length > 0) {
      organizationsListSignal.update({
        dropdownFilters: filtersFromUrl,
        appliedFilters: filtersFromUrl,
        viewType: currentViewType,
      });
    } else if (currentViewType) {
      const savedFilters = organizationsListSignal.value.filtersByViewType[currentViewType];
      if (savedFilters && Object.keys(savedFilters.appliedFilters).length > 0) {
        organizationsListSignal.update({
          dropdownFilters: savedFilters.dropdownFilters,
          appliedFilters: savedFilters.appliedFilters,
          viewType: currentViewType,
        });
      } else {
        organizationsListSignal.update({
          dropdownFilters: {},
          appliedFilters: {},
          viewType: currentViewType,
        });
      }
    }

    if (refetchingFilter) {
      if (refetchingFilter(location)) {
        return fetchAndSetOrganizations(currentPage);
      } else {
        return undefined;
      }
    }

    return fetchAndSetOrganizations(currentPage);
  }, [location.search, location.pathname, currentPage, refetchingFilter]);

  useEffect(() => {
    init();
  }, [init]);

  return (
    <>
      <OrganizationDetailsModal showLinkToPrograms={undefined} />
      <ShareOrganizationModal />
      <div className="d-flex justify-content-end">
        {location.pathname.startsWith('/edo/business') &&
          <Button 
            onClick={() => downloadSignupReport()}
            >
            Download Signup Report
          </Button>
        }
      </div>
      <div
        className={`mb-16 d-flex align-items-center ${
          tableTitle ? 'justify-content-between' : 'justify-content-start'
        }`}
      >
        {tableTitle && <h5>{tableTitle}</h5>}
        <div className="d-flex align-items-center">
          <SimpleTableFilterDropdown
            filtersState={organizationsListSignal.value.dropdownFilters}
            onChange={handleOrganizationsFilterChange}
            onApply={(filters) => {
              handleOrganizationsAppliedFiltersChange(filters);
              fetchAndSetOrganizations(currentPage);
            }}
            onClearFilterInputs={resetOrganizationsFilterInputs}
            filters={
              location.pathname.startsWith('/edo/business')
                ? [...FILTERS, ...BUSINESS_OWNERSHIP_FILTERS]
                : FILTERS
            }
          />
          {count != null && (
            <span className="ml-3 text-muted">
              {count} result{count !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
      {records.length === 0 && location.pathname.includes('/edo/lenders') && (() => {
        const userData = userAccountSignal.value?.userData;
        const { USER_ROLES } = $appSettings.value.constants;
        const edoUserLenderAccesses = (userData as any)?.edoUserLenderAccesses || [];
        const isStaffOrViewer = userData?.role === USER_ROLES.staff || userData?.role === USER_ROLES.viewer;
        const hasNoLenderAccess = isStaffOrViewer && edoUserLenderAccesses.length === 0;

        return (
          <div className="p-32 text-center text-muted">
            {hasNoLenderAccess ? (
              <p className="mb-0">
                You have not been granted access to view any Lenders. Please contact your administrator to request access.
              </p>
            ) : (
              <p className="mb-0">No Lenders found.</p>
            )}
          </div>
        );
      })()}
      {!(records.length === 0 && location.pathname.includes('/edo/lenders')) && (
        <>
          <SimpleTable
            headers={HEADERS}
            rows={records.map((record) => ({
              ...getRow(record),
              action: ({ key }: { key: string }) => (
                <td className="text-right" key={key}>
                  {record.portalType === PORTAL_TYPES.business && (
                    <Button
                      className="rounded mr-8"
                      size="sm"
                      variant="outline-primary"
                      onClick={() => showShareOrganizationModal(record.business)}
                    >
                      Share
                    </Button>
                  )}
                  <Button
                    className="rounded"
                    size="sm"
                    variant="outline-dark"
                    onClick={() => handleDetailClick(record, newPath)}
                  >
                    Detail
                  </Button>
                </td>
              ),
            }))}
          />
          {totalPages > 1 && (
            <PaginationItems
              className="justify-content-center mt-24"
              totalPages={totalPages}
            />
          )}
        </>
      )}
    </>
  );
};

export default OrganizationsList;
